import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn';

const bandStyles = {
  '1k': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  '2k': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  '3k': 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  '5k': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  '10k': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  'rare': 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

function useBandLabels() {
  const { t } = useTranslation();
  return {
    '1k': t('frequency.top1k'),
    '2k': t('frequency.top2k'),
    '3k': t('frequency.top3k'),
    '5k': t('frequency.top5k'),
    '10k': t('frequency.top10k'),
    'rare': t('frequency.rare'),
  };
}

export default function FrequencyBadge({ band, frequencyZipf, size = 'md', className }) {
  const { t } = useTranslation();
  const bandLabels = useBandLabels();
  if (!band) return null;

  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3 py-1.5 text-sm',
  };

  const handleMouseEnter = () => {
    if (!frequencyZipf) return;
    clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  if (!frequencyZipf) {
    return (
      <span className={cn(
        'font-medium rounded',
        bandStyles[band] || bandStyles.rare,
        sizeClasses[size],
        className
      )}>
        {bandLabels[band] || band}
      </span>
    );
  }

  return (
    <span
      className="relative inline-flex items-center"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className={cn(
        'font-medium rounded cursor-help',
        bandStyles[band] || bandStyles.rare,
        sizeClasses[size],
        className
      )}>
        {bandLabels[band] || band}
      </span>

      {isOpen && (
        <div
          className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 z-50 whitespace-nowrap rounded-md shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5"
          onMouseEnter={() => clearTimeout(timeoutRef.current)}
          onMouseLeave={handleMouseLeave}
        >
          <p className="text-xs text-gray-600 dark:text-gray-300">
            {t('frequency.scoreLabel', { score: frequencyZipf.toFixed(2) })}
          </p>
        </div>
      )}
    </span>
  );
}
